import { axios } from "../../api";
import { renderTemplatePreviewBlob } from "../../reports-and-forms/reports-and-forms";
import type { ItemList } from "../types";

export const INVENTORY_LABEL_TEMPLATE = "inventory_label/inventory_label.html";

export interface InventoryLabelPdfV2BuildResult {
  blob: Blob;
  filename: string;
}

function formatQuantity(
  value: number | string | null | undefined,
  decimals = 2
): string {
  const num = Number.parseFloat(String(value ?? "0"));
  if (Number.isNaN(num)) return "";
  return num.toFixed(decimals);
}

/** Legacy labels often show a two-letter condition code (e.g. NEW → NE). */
function formatConditionForLabel(condition?: string | null): string {
  const value = (condition ?? "").trim();
  if (!value) return "";
  if (value.length <= 2) return value.toUpperCase();
  return value.substring(0, 2).toUpperCase();
}

function formatSerialForLabel(serial?: string | null): string {
  const value = (serial ?? "").trim();
  return value || "N/A";
}

function resolvePoNumber(item: ItemList): string {
  const orderId = item.order_detail?.order?.id;
  if (orderId != null && Number(orderId) > 0) {
    return String(orderId);
  }
  const purchaseNumber = (item.purchase_number ?? "").trim();
  return purchaseNumber;
}

function resolveWoNumber(item: ItemList): string {
  if (item.job_id != null && Number(item.job_id) > 0) {
    return String(item.job_id);
  }
  if (item.from_job_id != null && Number(item.from_job_id) > 0) {
    return String(item.from_job_id);
  }
  return "";
}

function buildInventoryLabelBarcodeUrl(itemNumber: string): string {
  const data = encodeURIComponent(itemNumber);
  return `https://bwipjs-api.metafloor.com/?bcid=code128&text=${data}&scale=2&height=8&includetext=false`;
}

/** Build stationery context for inventory_label/inventory_label.html. */
export function buildInventoryLabelContext(
  item: ItemList,
  options?: {
    globalConfig?: { quantity_rounding?: number; date_format?: string; company?: string };
  }
): Record<string, unknown> {
  const quantityRounding = Number(options?.globalConfig?.quantity_rounding ?? 2);

  const itemNumber = String(item.id);

  const context: Record<string, unknown> = {
    item_number: itemNumber,
    barcode_url: buildInventoryLabelBarcodeUrl(itemNumber),
    part_number: item.part?.number ?? "",
    part_desc: (item.part?.description ?? "").trim(),
    part_superseded_by: item.part?.part_superseded_by ?? "",
    alt_part_1: item.part?.alt_part_1 ?? "",
    alt_part_2: item.part?.alt_part_2 ?? "",
    batch_no: item.batch?.number ?? "",
    location: item.location ?? "",
    store: item.store ?? "",
    shelf: item.shelf ?? "",
    condition: formatConditionForLabel(item.condition),
    quantity: formatQuantity(item.quantity, quantityRounding),
    serial_no: formatSerialForLabel(item.serial_number),
    expiry_date: item.shelf_expiry ?? "",
    po_no: resolvePoNumber(item),
    wo_no: resolveWoNumber(item),
  };

  if (options?.globalConfig?.date_format || options?.globalConfig?.company) {
    context.client_config = {
      ...(options?.globalConfig?.date_format
        ? { date_format: options.globalConfig.date_format }
        : {}),
      ...(options?.globalConfig?.company
        ? { company: options.globalConfig.company }
        : {}),
    };
  }

  return context;
}

export async function buildInventoryLabelPdfViaStationeryV2(args: {
  itemId: number;
  globalConfig?: { quantity_rounding?: number; date_format?: string; company?: string };
  /** When the grid row already has full item data, skip the GET. */
  item?: ItemList;
}): Promise<InventoryLabelPdfV2BuildResult> {
  const { itemId, globalConfig, item: itemFromRow } = args;

  let item = itemFromRow;
  if (!item) {
    const resp = await axios.get<ItemList>(`/v6/items/${itemId}`, {
      id: `get-item-${itemId}-inventory-label`,
      cache: false,
    });
    axios.storage.remove(`get-item-${itemId}-inventory-label`);
    item = resp.data;
  }

  const context = buildInventoryLabelContext(item, { globalConfig });
  const blob = await renderTemplatePreviewBlob(
    INVENTORY_LABEL_TEMPLATE,
    "pdf",
    context
  );

  return {
    blob,
    filename: `inventory-label-${itemId}.pdf`,
  };
}
